import ChangeCommentModal from '@/components/change-comment-modal';
import { useOpenClient } from '@/hooks/use-open-client';
import VbivStatus from '@/components/vbiv-status';
import type { IBalance } from '@/entities/balance.entity';
import { BanknoteIcon, BellIcon, CalendarClockIcon, CircleDollarSignIcon, ContainerIcon, HashIcon, MessageCircleIcon, TextIcon } from 'lucide-react';
import { memo, useCallback, useEffect, useState, type MouseEvent } from 'react';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);
const money = new Intl.NumberFormat('en-US', { style: 'decimal', maximumFractionDigits: 0 });

const BalanceCard = memo(function BalanceCard({ balance }: { balance: IBalance }) {
	const [commentText, setCommentText] = useState(balance.comment ?? '');

	const { openClient } = useOpenClient();
	const goToClient = useCallback(() => {
		openClient(balance.clientId, { listType: 'balances', clientId: balance.clientId, itemId: balance.id });
	}, [balance.clientId, balance.id, openClient]);
	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	useEffect(() => {
		if (balance.comment !== commentText) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setCommentText(balance.comment || '');
		}
	}, [balance.comment]);

	return (
		<div
			role='button'
			tabIndex={0}
			className='relative p-2 bg-card rounded-md flex flex-col gap-1.5 border-border border border-l-2 overflow-hidden cursor-pointer active:bg-accent/40'
			onClick={goToClient}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					goToClient();
				}
			}}
		>
			<div className='flex justify-between items-center'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<MessageCircleIcon size={16} className='opacity-40' />
					</div>
					<span className='flex break-all items-center' onClick={e => e.stopPropagation()}>
						{balance.comment && (
							<span className='text-sm hover:bg-accent cursor-pointer select-none text-[#e89bff]' onClick={e => handleCopy(e, balance.comment || '')}>
								{balance.comment}
							</span>
						)}
						<ChangeCommentModal currentComment={balance.comment} balance={balance} />
					</span>
				</span>
				<div onClick={e => e.stopPropagation()}>
					<VbivStatus clientId={balance.clientId} takeStatus={balance.takeStatus} takenFrom={balance.takenFrom} />
				</div>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center break-all'>
					<div className='w-[18px]'>
						<HashIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, '#' + balance.clientId)}>
						{balance.clientId}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<ContainerIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, balance.buildId)}>
						{balance.buildId}
					</span>
				</span>
			</div>
			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<TextIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm '>{balance.text}</span>
					{balance.source === 'push' ? (
						<span className='inline-flex items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground'>
							<BellIcon size={10} />
							Push
						</span>
					) : null}
				</span>
			</div>

			{balance.cardNumber && (
				<div className='flex justify-between'>
					<span className='flex gap-1 items-center'>
						<div className='w-[18px]'>
							<BanknoteIcon size={16} className='opacity-40' />
						</div>
						<span className='text-sm hover:bg-accent cursor-pointer select-none text-chart-3 font-semibold ' onClick={e => handleCopy(e, balance.cardNumber)}>
							{balance.cardNumber}
						</span>
					</span>
				</div>
			)}

			{balance.cardBalance != null && (
				<div className='flex justify-between'>
					<span className='flex gap-1 items-center'>
						<div className='w-[18px]'>
							<CircleDollarSignIcon size={16} className='opacity-40' />
						</div>
						<span
							className='text-sm hover:bg-accent cursor-pointer select-none text-destructive font-semibold'
							onClick={e => handleCopy(e, String(balance.cardBalance))}
						>
							{money.format(balance.cardBalance)}
						</span>
					</span>
				</div>
			)}

			<span className='flex gap-1 items-center'>
				<div className='w-[18px]'>
					<CalendarClockIcon size={16} className='opacity-40' />
				</div>
				<span className='text-sm font-semibold opacity-40 '>Получен</span>
				<div className=' text-sm text-chart-2 opacity-95'>
					<TimeAgo date={balance.createdAt} formatter={formatter} />
				</div>
			</span>
		</div>
	);
});

export default BalanceCard;
